import { ConversationStatus } from '@provision/conversation-types';
import { useCallback, useMemo } from 'react';
import { Conversation, useGroupRules, UserGroupRules } from 'src/graphql';
import { useCanUpdateConversation } from 'src/graphql/entities/conversation/use-can-update-conversation';
import { useConversationUpdate } from 'src/graphql/entities/conversation/use-conversation-update';
import {
  canRestoreSnooze,
  useRestoreConversationStatus,
} from 'src/graphql/entities/conversation/use-restore-conversation-status';
import {
  SwipeAction,
  SwipeActionKind,
} from 'src/pages/CustomViews/custom-views-mobile-card-list/card-swipe.types';
import { Modals, useModalMethods } from 'src/store/modals';
import { useSnackbar } from 'src/utils/hooks';

export type SwipeableConversation = Pick<
  Conversation,
  | 'conversationId'
  | 'friendlyId'
  | 'partnerId'
  | 'conversationStatusId'
  | 'snoozeUntil'
  | 'isDeleted'
>;

export const getConversationSwipeActions = (conversationStatusId: string): SwipeAction[] => {
  const status = conversationStatusId as ConversationStatus;

  return [
    {
      kind: SwipeActionKind.Snooze,
      label: status === ConversationStatus.Snoozed ? 'Unsnooze' : 'Snooze',
      icon: 'alarm-clock',
    },
    status === ConversationStatus.Solved
      ? { kind: SwipeActionKind.Solve, label: 'Reopen', icon: 'rotate-ccw' }
      : { kind: SwipeActionKind.Solve, label: 'Solve', icon: 'circle-check' },
  ];
};

export const useConversationSwipeActions = ({
  conversationId,
  friendlyId,
  partnerId,
  conversationStatusId,
  snoozeUntil,
  isDeleted,
}: SwipeableConversation) => {
  const { groupRules } = useGroupRules();
  const { showModal: showSnoozeModal } = useModalMethods(Modals.SnoozeConversationModal);
  const { updateConversation } = useConversationUpdate();
  const restoreConversationStatus = useRestoreConversationStatus();
  const { showSuccessAlert } = useSnackbar();

  const canUpdateConversation = useCanUpdateConversation({ conversationStatusId, isDeleted });
  const canUpdateStatus =
    groupRules[UserGroupRules.AccessToUpdateConversationStatus] === true ||
    groupRules[UserGroupRules.AccessToUpdateAllConversationStatuses] === true;

  const setStatus = useCallback(
    async (status: string) => {
      // only the status goes in: the hook writes every field it is given back to the server
      // and the cache, and the row's display-mapped subject must not replace the real one
      const result = await updateConversation({
        conversationId,
        conversationStatusId: status,
      } as Conversation);

      // the hook's onError turns a failure into an alert, so the promise resolves either way
      return !!result?.data && !result.errors?.length;
    },
    [conversationId, updateConversation],
  );

  const runAction = useCallback(
    async (kind: SwipeActionKind) => {
      const status = conversationStatusId as ConversationStatus;
      const restorePrevious = () =>
        void restoreConversationStatus({ conversationId, conversationStatusId, snoozeUntil });

      if (kind === SwipeActionKind.Snooze && status === ConversationStatus.Snoozed) {
        if (!(await setStatus(ConversationStatus.Open))) return;

        showSuccessAlert(`Unsnoozed ${friendlyId}.`, {
          onUndo: canRestoreSnooze({ snoozeUntil }) ? restorePrevious : undefined,
        });
        return;
      }

      if (kind === SwipeActionKind.Snooze) {
        showSnoozeModal({
          conversationId,
          conversationStatusId,
          snoozeUntil,
          partnerId,
          friendlyId,
        });
        return;
      }

      const isReopen = status === ConversationStatus.Solved;
      if (!(await setStatus(isReopen ? ConversationStatus.Open : ConversationStatus.Solved))) {
        return;
      }

      showSuccessAlert(isReopen ? `Reopened ${friendlyId}.` : `Solved ${friendlyId}.`, {
        onUndo: restorePrevious,
      });
    },
    [
      conversationId,
      conversationStatusId,
      friendlyId,
      partnerId,
      restoreConversationStatus,
      setStatus,
      showSnoozeModal,
      showSuccessAlert,
      snoozeUntil,
    ],
  );

  const actions = useMemo(
    () => getConversationSwipeActions(conversationStatusId),
    [conversationStatusId],
  );

  return {
    isSwipeable: canUpdateConversation && canUpdateStatus,
    actions,
    runAction,
  };
};
